import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Logo } from '../../components/Logo';
import { apiPost, ApiError } from '../../lib/api';

type CountryCode = 'UK' | 'AE-MAIN' | 'AE-FZ' | 'IN' | 'SG' | 'BVI' | 'KY' | 'OTHER';
type EntityType = 'Ltd' | 'PLC' | 'LLP' | 'FZ-LLC' | 'Other';
type Sector = 'Shipping/Logistics' | 'Energy/Renewables' | 'Real Estate' | 'Infrastructure' | 'Commodities' | 'PE/VC' | 'Family Office' | 'Technology' | 'Other';
type ContactRole = 'Director' | 'CEO' | 'CFO' | 'General Counsel' | 'Treasurer' | 'Authorised representative';

interface SignupState {
  legal_name: string;
  registration_number: string;
  country: CountryCode;
  entity_type: EntityType;
  sector: Sector | '';
  contact_name: string;
  contact_role: ContactRole | '';
  contact_email: string;
  contact_phone: string;
  body_corporate_confirmed: boolean;
  sophisticated_self_cert: boolean;
  not_advice_acknowledged: boolean;
  terms_accepted: boolean;
}

const INITIAL: SignupState = {
  legal_name: '', registration_number: '', country: 'UK', entity_type: 'Ltd', sector: '',
  contact_name: '', contact_role: '', contact_email: '', contact_phone: '',
  body_corporate_confirmed: false, sophisticated_self_cert: false, not_advice_acknowledged: false, terms_accepted: false,
};

// Dial-code options for the contact-phone split field. Curated for Omega's
// target geographies (UK / EU / GCC / India / SEA) plus the major Americas
// and APAC markets. The `key` distinguishes countries that share a dial code
// (e.g. +1 → US / CA), while `code` is the value submitted with the number.
const DIAL_CODES: { key: string; flag: string; code: string; name: string }[] = [
  { key: 'GB', flag: '🇬🇧', code: '+44',  name: 'United Kingdom' },
  { key: 'AE', flag: '🇦🇪', code: '+971', name: 'UAE' },
  { key: 'IN', flag: '🇮🇳', code: '+91',  name: 'India' },
  { key: 'SG', flag: '🇸🇬', code: '+65',  name: 'Singapore' },
  { key: 'US', flag: '🇺🇸', code: '+1',   name: 'United States' },
  { key: 'CA', flag: '🇨🇦', code: '+1',   name: 'Canada' },
  { key: 'IE', flag: '🇮🇪', code: '+353', name: 'Ireland' },
  { key: 'FR', flag: '🇫🇷', code: '+33',  name: 'France' },
  { key: 'DE', flag: '🇩🇪', code: '+49',  name: 'Germany' },
  { key: 'NL', flag: '🇳🇱', code: '+31',  name: 'Netherlands' },
  { key: 'IT', flag: '🇮🇹', code: '+39',  name: 'Italy' },
  { key: 'ES', flag: '🇪🇸', code: '+34',  name: 'Spain' },
  { key: 'CH', flag: '🇨🇭', code: '+41',  name: 'Switzerland' },
  { key: 'SE', flag: '🇸🇪', code: '+46',  name: 'Sweden' },
  { key: 'NO', flag: '🇳🇴', code: '+47',  name: 'Norway' },
  { key: 'DK', flag: '🇩🇰', code: '+45',  name: 'Denmark' },
  { key: 'FI', flag: '🇫🇮', code: '+358', name: 'Finland' },
  { key: 'BE', flag: '🇧🇪', code: '+32',  name: 'Belgium' },
  { key: 'LU', flag: '🇱🇺', code: '+352', name: 'Luxembourg' },
  { key: 'SA', flag: '🇸🇦', code: '+966', name: 'Saudi Arabia' },
  { key: 'QA', flag: '🇶🇦', code: '+974', name: 'Qatar' },
  { key: 'KW', flag: '🇰🇼', code: '+965', name: 'Kuwait' },
  { key: 'BH', flag: '🇧🇭', code: '+973', name: 'Bahrain' },
  { key: 'OM', flag: '🇴🇲', code: '+968', name: 'Oman' },
  { key: 'EG', flag: '🇪🇬', code: '+20',  name: 'Egypt' },
  { key: 'ZA', flag: '🇿🇦', code: '+27',  name: 'South Africa' },
  { key: 'AU', flag: '🇦🇺', code: '+61',  name: 'Australia' },
  { key: 'NZ', flag: '🇳🇿', code: '+64',  name: 'New Zealand' },
  { key: 'MY', flag: '🇲🇾', code: '+60',  name: 'Malaysia' },
  { key: 'ID', flag: '🇮🇩', code: '+62',  name: 'Indonesia' },
  { key: 'TH', flag: '🇹🇭', code: '+66',  name: 'Thailand' },
  { key: 'PH', flag: '🇵🇭', code: '+63',  name: 'Philippines' },
  { key: 'VN', flag: '🇻🇳', code: '+84',  name: 'Vietnam' },
  { key: 'HK', flag: '🇭🇰', code: '+852', name: 'Hong Kong' },
  { key: 'JP', flag: '🇯🇵', code: '+81',  name: 'Japan' },
  { key: 'KR', flag: '🇰🇷', code: '+82',  name: 'South Korea' },
  { key: 'CN', flag: '🇨🇳', code: '+86',  name: 'China' },
  { key: 'BR', flag: '🇧🇷', code: '+55',  name: 'Brazil' },
  { key: 'MX', flag: '🇲🇽', code: '+52',  name: 'Mexico' },
  { key: 'TR', flag: '🇹🇷', code: '+90',  name: 'Turkey' },
];

// Flag image via flagcdn.com — works cross-platform (Windows native <select>
// strips regional-indicator emoji to letters, so we can't use 🇬🇧 inside a
// native control). 2x retina-friendly via the `w40` set.
function FlagImg({ iso }: { iso: string }) {
  const code = iso.toLowerCase();
  return (
    <img
      src={`https://flagcdn.com/w40/${code}.png`}
      srcSet={`https://flagcdn.com/w80/${code}.png 2x`}
      width={20}
      height={15}
      alt=""
      style={{ borderRadius: 2, display: 'block', objectFit: 'cover' }}
    />
  );
}

// Custom dial-code dropdown. Native <select> can't render <img> inside <option>,
// so we build a button + popup-listbox pair. Click-outside + Escape close it.
function DialCodeDropdown({
  value, onChange, ariaInvalid,
}: { value: string; onChange: (key: string) => void; ariaInvalid?: boolean }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const current = DIAL_CODES.find((c) => c.key === value) ?? DIAL_CODES[0]!;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="dial-dd">
      <button
        type="button"
        className="dial-dd-btn"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={ariaInvalid}
      >
        <FlagImg iso={current.key} />
        <span className="dial-dd-code">{current.code}</span>
        <span className="dial-dd-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <ul className="dial-dd-list" role="listbox">
          {DIAL_CODES.map((c) => (
            <li
              key={c.key}
              role="option"
              aria-selected={c.key === value}
              className={c.key === value ? 'sel' : ''}
              onClick={() => { onChange(c.key); setOpen(false); }}
            >
              <FlagImg iso={c.key} />
              <span className="dial-dd-name">{c.name}</span>
              <span className="dial-dd-code">{c.code}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function PortalSignUp() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [d, setD] = useState<SignupState>(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [reference, setReference] = useState<string | null>(null);

  // Telephone is composed from a dial-code dropdown + a local-digits input.
  // `d.contact_phone` is kept in sync so the API receives a single combined
  // string (`"+44 7700900000"`). Default dial code = UK to match `country`.
  const [dialKey, setDialKey] = useState('GB');
  const [phoneLocal, setPhoneLocal] = useState('');
  const updatePhone = (nextKey: string, nextLocal: string) => {
    setDialKey(nextKey);
    setPhoneLocal(nextLocal);
    const dial = DIAL_CODES.find((c) => c.key === nextKey)?.code ?? '';
    setD((s) => ({ ...s, contact_phone: nextLocal ? `${dial} ${nextLocal}` : '' }));
  };

  const set = <K extends keyof SignupState>(k: K) => (e: { target: { value: string } }) =>
    setD((s) => ({ ...s, [k]: e.target.value as SignupState[K] }));
  const toggle = (k: 'body_corporate_confirmed' | 'sophisticated_self_cert' | 'not_advice_acknowledged' | 'terms_accepted') => () =>
    setD((s) => ({ ...s, [k]: !s[k] }));

  // Inline-validation state — fields are only flagged red after the user has
  // moved on from them once (blurred), so they don't see errors mid-typing.
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const markTouched = (field: string) => () =>
    setTouched((t) => ({ ...t, [field]: true }));

  // Any valid email is accepted — work or personal. The signup form's only
  // email rule is format validity; domain filtering happens nowhere on the
  // server either (see clients.ts /signup).
  const validateEmail = (raw: string): string | null => {
    const v = raw.trim();
    if (v === '') return null; // empty fields aren't flagged red — just block Continue
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Enter a valid email address.';
    return null;
  };
  const validatePhone = (raw: string): string | null => {
    const v = raw.trim();
    if (v === '') return null;
    // Allow digits + the usual formatting characters; anything else is wrong.
    if (!/^[+\d\s\-()]+$/.test(v)) {
      return 'Phone may contain digits, spaces, +, - and ( ) only.';
    }
    // Count the digit characters specifically — formatting (spaces, dashes,
    // parens, the leading +) doesn't add to the actual phone-number length.
    const digitCount = v.replace(/\D/g, '').length;
    if (digitCount < 10) {
      return `Phone number must have at least 10 digits (currently ${digitCount}).`;
    }
    if (digitCount > 15) {
      // E.164 standard max length is 15 digits.
      return 'Phone number can have at most 15 digits.';
    }
    return null;
  };

  const emailError = validateEmail(d.contact_email);
  const phoneError = validatePhone(d.contact_phone);

  const ready =
    step === 1 ? d.legal_name && d.registration_number && d.sector :
    step === 2 ? d.contact_name && d.contact_email && d.contact_phone && d.contact_role && !emailError && !phoneError :
    d.body_corporate_confirmed && d.sophisticated_self_cert && d.not_advice_acknowledged && d.terms_accepted;

  const submit = async () => {
    if (!ready || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await apiPost<{ ok: true; client_id: string; status: string }>('/api/clients/signup', d);
      setReference(r.client_id);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : 'Application failed. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <main className="auth-shell flow">
        <div className="auth-card wide">
          <div className="auth-card-head">
            <Link to="/" aria-label="Omega Trade home" className="auth-logo"><Logo /></Link>
            <span className="chip green" style={{ marginBottom: 4, alignSelf: 'flex-start' }}>Application received</span>
            <h1>We’ll be in touch.</h1>
            <div className="sub">
              Compliance verifies every application before portal credentials are issued. Expect a
              magic-link sign-in within 1 UK business day at <span style={{ fontFamily: 'var(--mono)', color: 'var(--fg)' }}>{d.contact_email}</span>.
              We've sent a confirmation email — check spam if it doesn't arrive within a few minutes.
            </div>
          </div>
          {reference && (
            <div style={{ padding: '10px 14px', background: 'var(--bg-soft)', borderRadius: 8, fontSize: 13 }}>
              Application reference: <span style={{ fontFamily: 'var(--mono)', color: 'var(--fg)' }}>{reference}</span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn accent" onClick={() => navigate('/sign-in')}>Go to sign in</button>
            <Link className="btn ghost" to="/">Back to omegatrade.co.uk</Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="auth-shell flow">
      <div className="auth-card wide">
        <div className="auth-card-head">
          <Link to="/" aria-label="Omega Trade home" className="auth-logo"><Logo /></Link>
          <h1>Open a corporate account</h1>
          <div className="sub">
            Step {step} of 3 — {step === 1 ? 'Company' : step === 2 ? 'Authorised contact' : 'Eligibility'}.
          </div>
        </div>

          <div className="statusbar" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginTop: 6, marginBottom: 18 }}>
            {['Company', 'Contact', 'Eligibility'].map((s, i) => (
              <div key={s} className={`step ${step - 1 > i ? 'done' : step - 1 === i ? 'now' : ''}`}>
                {String(i + 1).padStart(2, '0')} {s}
              </div>
            ))}
          </div>

          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="field">
                <label>Legal company name</label>
                <input value={d.legal_name} onChange={set('legal_name')} placeholder="e.g. Helios Marine Ltd" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'start' }}>
                <div className="field">
                  <label>Company / registration no.</label>
                  <input value={d.registration_number} onChange={set('registration_number')} placeholder="09812334" />
                </div>
                <div className="field">
                  <label>Country of incorporation</label>
                  <select value={d.country} onChange={set('country')}>
                    <option value="UK">United Kingdom</option>
                    <option value="AE-MAIN">UAE (Mainland)</option>
                    <option value="AE-FZ">UAE (Free Zone)</option>
                    <option value="IN">India</option>
                    <option value="SG">Singapore</option>
                    <option value="BVI">BVI</option>
                    <option value="KY">Cayman</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'start' }}>
                <div className="field">
                  <label>Entity type</label>
                  <select value={d.entity_type} onChange={set('entity_type')}>
                    <option value="Ltd">Limited Company</option>
                    <option value="PLC">PLC</option>
                    <option value="LLP">LLP</option>
                    <option value="FZ-LLC">Free Zone LLC</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="field">
                  <label>Sector / vertical</label>
                  <select
                    value={d.sector}
                    onChange={set('sector')}
                    style={{ color: 'var(--fg)' }}
                  >
                    <option value="" disabled hidden>Select sector…</option>
                    <option value="Shipping/Logistics">Shipping / Logistics</option>
                    <option value="Energy/Renewables">Energy / Renewables</option>
                    <option value="Real Estate">Real Estate</option>
                    <option value="Infrastructure">Infrastructure</option>
                    <option value="Commodities">Commodities</option>
                    <option value="PE/VC">Private Equity / VC</option>
                    <option value="Family Office">Family Office</option>
                    <option value="Technology">Technology</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="field">
                <label>Full name</label>
                <input value={d.contact_name} onChange={set('contact_name')} placeholder="Enter your name" />
              </div>
              <div className="field">
                <label>Your Role</label>
                <select
                  value={d.contact_role}
                  onChange={set('contact_role')}
                  style={{ color: 'var(--fg)' }}
                >
                  <option value="" disabled hidden>Select role…</option>
                  <option>Director</option>
                  <option>CEO</option>
                  <option>CFO</option>
                  <option>General Counsel</option>
                  <option>Treasurer</option>
                  <option>Authorised representative</option>
                </select>
              </div>
              <div className="field">
                <label>Email</label>
                <input
                  type="email"
                  value={d.contact_email}
                  onChange={set('contact_email')}
                  onBlur={markTouched('contact_email')}
                  placeholder="Enter your email"
                  aria-invalid={Boolean(touched.contact_email && emailError)}
                  style={touched.contact_email && emailError
                    ? { borderColor: 'var(--red, #c0392b)' }
                    : undefined}
                />
                {touched.contact_email && emailError && (
                  <div className="hint" style={{ color: 'var(--red, #c0392b)' }}>
                    {emailError}
                  </div>
                )}
              </div>
              <div className="field">
                <label>Contact telephone number</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <DialCodeDropdown
                    value={dialKey}
                    onChange={(k) => updatePhone(k, phoneLocal)}
                  />
                  <input
                    value={phoneLocal}
                    onChange={(e) => updatePhone(dialKey, e.target.value)}
                    onBlur={markTouched('contact_phone')}
                    placeholder="Enter your telephone number"
                    inputMode="tel"
                    aria-invalid={Boolean(touched.contact_phone && phoneError)}
                    style={{
                      flex: 1,
                      ...(touched.contact_phone && phoneError
                        ? { borderColor: 'var(--red, #c0392b)' }
                        : null),
                    }}
                  />
                </div>
                {touched.contact_phone && phoneError && (
                  <div className="hint" style={{ color: 'var(--red, #c0392b)' }}>
                    {phoneError}
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 3 && (() => {
            const allFour =
              d.body_corporate_confirmed &&
              d.sophisticated_self_cert &&
              d.not_advice_acknowledged &&
              d.terms_accepted;
            const toggleAll = () =>
              setD((s) => {
                const next = !(
                  s.body_corporate_confirmed &&
                  s.sophisticated_self_cert &&
                  s.not_advice_acknowledged &&
                  s.terms_accepted
                );
                return {
                  ...s,
                  body_corporate_confirmed: next,
                  sophisticated_self_cert: next,
                  not_advice_acknowledged: next,
                  terms_accepted: next,
                };
              });
            return (
              <>
                <p style={{ fontSize: 13, color: 'var(--fg-soft)', lineHeight: 1.65, margin: 0 }}>
                  Confirmation of eligibility is required before portal credentials can be issued.
                </p>
                <ul style={{
                  margin: '14px 0 0',
                  paddingLeft: 22,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  fontSize: 13,
                  color: 'var(--fg-soft)',
                  lineHeight: 1.55,
                }}>
                  <li>
                    <strong style={{ color: 'var(--fg)' }}>Body corporate.</strong>{' '}
                    Limited Co, PLC or LLP — not a sole trader, partnership of fewer than 4, or individual.
                  </li>
                  <li>
                    <strong style={{ color: 'var(--fg)' }}>FPO sophisticated / HNW corporate.</strong>{' '}
                    Qualifies under FPO "investment professional" or "high net worth body corporate" exemption.
                  </li>
                  <li>
                    <strong style={{ color: 'var(--fg)' }}>Not investment advice.</strong>{' '}
                    Omega is not FCA-authorised and does not provide investment advice. All work is consultancy, research, document preparation, or Article-15/29 introductions.
                  </li>
                  <li>
                    <strong style={{ color: 'var(--fg)' }}>Terms of Business.</strong>{' '}
                    I have read and accept the{' '}
                    <Link to="/regulatory" style={{ color: 'var(--accent)' }} target="_blank" rel="noreferrer">
                      Regulatory Position
                    </Link>.
                  </li>
                </ul>
                <label
                  className={`checkbox ${allFour ? 'on' : ''}`}
                  onClick={toggleAll}
                  style={{ marginTop: 14 }}
                >
                  <span className="box" />
                  <span>I confirm all the eligibility criteria are met.</span>
                </label>
                <div className="regnote" style={{ marginTop: 14 }}>
                  Compliance verifies every application. Portal access is issued by magic-link within 1 UK business day.
                </div>
              </>
            );
          })()}

          {error && <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 12 }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24, gap: 16 }}>
            {step > 1 ? (
              <button className="btn ghost" type="button" onClick={() => setStep(step - 1)}>← Back</button>
            ) : (
              <Link
                to="/sign-in"
                style={{
                  fontSize: 13,
                  color: 'var(--fg-soft)',
                  alignSelf: 'center',
                  display: 'inline-flex',
                  gap: 4,
                }}
              >
                Already have access?{' '}
                <span style={{ color: 'var(--accent)', textDecoration: 'underline', textUnderlineOffset: 2 }}>
                  Sign in
                </span>
              </Link>
            )}
            {step < 3 ? (
              <button className="btn accent" type="button" disabled={!ready} onClick={() => setStep(step + 1)}>Continue →</button>
            ) : (
              <button className="btn accent" type="button" disabled={!ready || submitting} onClick={submit}>
                {submitting ? 'Submitting…' : 'Submit application →'}
              </button>
            )}
          </div>
      </div>
    </main>
  );
}
